import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectorRef,
	Component,
	DoCheck,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { LazyPaymentLoader, lazyPaymentLoaderToken } from 'go-modules/payment-panel/lazy-payment-loader.service';
import { PAYMENT_TYPES, PURCHASE_TYPES } from 'go-modules/payment-panel/payment-panel.controller';
import {
	SelectedInterface,
	SelectedService,
	selectedServiceToken
} from 'go-modules/services/selected/selected.service';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	filter,
	finalize,
	map,
	Observable,
	Subject,
	Subscription,
	takeUntil
} from 'rxjs';
import { BaseDataSource } from '@go-modules/classes/base-data-source';
import { ConsumeSeatResponse, NgxGroupService } from '@go-modules/services/group/group.service';
import {
	GroupSettingsPanelService,
	groupSettingsPanelServiceToken
} from 'go-modules/group-settings-panel/group-settings-panel.service';
import { groupToken } from 'go-modules/models/group-dep/group.factory';
import { NgxCourseService } from '@go-modules/services/course/course.service';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import dayjs from 'dayjs';
import { ActiveBrandService } from '@go-modules/services/active-brand';


export enum ZeroStates {
	DEACTIVATED_ROLE = 1,
	NO_ELIGIBLE_BILLING = 2,
	MAX_SEAT_REACHED = 3,
	COURSE_PAYMENT_WALL = 4
}

export interface ZeroStateChangedEvent {
	hasZeroState: boolean;
	isLoading: boolean;
	errorCode: number | null;
}

export interface CoursePaywallOnPayEvent {
	type: PAYMENT_TYPES;
}

@Component({
	selector: 'data-source-zero-state',
	templateUrl: './data-source-zero-state.component.html',
	styleUrls: ['./data-source-zero-state.component.scss'],
	standalone: false
})
// This hack uses DoCheck and OnChanges. Normally NEVER do this. Rather
// than fix the hack now, just ignore it here so we can proceed.
// eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
export class DataSourceZeroStateComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
	@Output() public zeroStateChanged = new EventEmitter<ZeroStateChangedEvent>();
	@Input() public dataSource: BaseDataSource<any, any>;

	public zeroStates = ZeroStates;
	public errorCode: number | null = null;
	public isLoading: boolean = false;
	public group;
	public user;
	public hasConsumingSeatError = false;
	public isLoaded: Observable<boolean>;
	public loadingSubscription: Subscription;
	public errorSubscription: Subscription;
	public hasZeroState$: Observable<boolean>;

	private hasZeroState$$ = new BehaviorSubject(false);
	private destroyed$$ = new Subject();
	private isDataSourceLoaded$$ = new BehaviorSubject<boolean>(false);
	private isConsumeSeatLoaded$$ = new BehaviorSubject<boolean>(true);

	public get hasErrorState () {
		return !isNaN(this.errorCode ?? undefined);
	}

	constructor (
		@Inject(groupToken) public groupModel,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(lazyPaymentLoaderToken) private paymentLoader: LazyPaymentLoader,
		@Inject(groupSettingsPanelServiceToken) private groupSettingsPanelService: GroupSettingsPanelService,
		@Inject('Window') private window: Window,
		private routerService: RouterService,
		private groupService: NgxGroupService,
		private cdr: ChangeDetectorRef,
		private courseService: NgxCourseService,
		public activeBrandService: ActiveBrandService
	) {
		this.hasZeroState$ = this.hasZeroState$$.asObservable();
	}

	public get isGroupIsACourse () {
		return this.group instanceof this.groupModel.GroupModel && this.group.isCourse();
	}

	// eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
	public ngDoCheck (): void {
		this.hasZeroState$$.next(this.hasErrorState || !!this.getZeroStateStatus());
	}

	// eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
	public ngOnInit (): void {
		this.group = this.selectedService.getGroup();
		this.user = this.userService.currentUser;

		this.consumeSeat();

		this.selectedService.selectedSubject
			.asObservable()
			.pipe(
				takeUntil(this.destroyed$$),
				filter((selected) => selected.group),
				filter((selected) => this.group?.getId() !== selected.group.getId()))
			.subscribe((selected: SelectedInterface) => {
				this.group = selected.group;
			});

		this.isLoaded =	combineLatest([
			this.isDataSourceLoaded$$.asObservable(),
			this.isConsumeSeatLoaded$$.asObservable()
		]).pipe(map(([dataSourceLoaded, consumeSeatLoaded]) => {
			return dataSourceLoaded && consumeSeatLoaded;
		}));

		combineLatest([this.isLoaded, this.hasZeroState$$.asObservable()])
			.pipe(
				takeUntil(this.destroyed$$),
				distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
				debounceTime(500)
			)
			.subscribe(([isLoaded, hasZeroState]) => {
				this.zeroStateChanged.emit({isLoading: !isLoaded, hasZeroState, errorCode: this.errorCode });
			});
	}

	// eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
	public ngOnDestroy () {
		this.errorSubscription?.unsubscribe();
		this.loadingSubscription?.unsubscribe();
		this.destroyed$$.next(true);
		this.destroyed$$.complete();
	}

	// eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.dataSource && this.dataSource) {
			this.errorSubscription?.unsubscribe();
			this.loadingSubscription?.unsubscribe();
			this.loadingSubscription = this.dataSource.loading$.subscribe((isLoading) => {
				if (isLoading) this.errorCode = null;
				this.isDataSourceLoaded$$.next(!isLoading);
			});

			this.errorSubscription = this.dataSource.onError$
				.subscribe((error: HttpErrorResponse) => {
					this.errorCode = error.status;
					this.cdr.detectChanges();
				});
		}
	}

	public getZeroStateStatus () {
		if(!this.isGroupIsACourse) {
			return;
		}

		if (this.group.isPresenterDeactivated()) {
			return ZeroStates.DEACTIVATED_ROLE;
		} else if(this.hasNoEligibleBilling()) {
			return ZeroStates.NO_ELIGIBLE_BILLING;
		} else if(this.shouldShowPaymentWall()) {
			return ZeroStates.COURSE_PAYMENT_WALL;
		} else if(this.hasConsumingSeatError) {
			return ZeroStates.MAX_SEAT_REACHED;
		}
	}

	public onPay ($event: CoursePaywallOnPayEvent) {
		return this.paymentLoader.openPayForm(
			this.user,
			this.group,
			() => this.window.location.reload(),
			null,
			$event.type,
			PURCHASE_TYPES.COURSE,
			null
		);
	}

	public onCredit () {
		this.groupService.consumeCredit(this.group.group_id)
			.then(() => this.window.location.reload())
			.catch((error) => this.errorCode = error.status);

	}

	public openGroupSettings () {
		this.groupSettingsPanelService.open(this.group, this.user, null, null)
			.result.then((result) => {
				if(result) {
					return this.courseService.getCourseLicense(this.group.group_id)
						.subscribe((license) => {
							this.group.license = license;
							this.selectedService.setLicense(this.group.license);
							this.hasZeroState$$.next(this.hasErrorState || !!this.getZeroStateStatus());
							this.cdr.detectChanges();
						});
				}
			});
	}

	/**
	 * Whether or not to show the group payment wall
	 *
	 * @returns {boolean}
	 */
	private shouldShowPaymentWall (): boolean {
		if (this.routerService.isRouteActive(ROUTE_NAMES.DASHBOARD_FOLDERS)) return false;
		let isPlanExpiring = false;

		const userPlan = this.userService.currentUser.plan;
		if (userPlan?.has_plan) {
			isPlanExpiring = dayjs(userPlan.ends_at).utc().isBefore(dayjs(this.group.end_date).utc());
		}

		return this.group.isCourse() && this.group.requiresPayment() && // group requires payment
			!this.group.hasPaid() &&  // has not paid
			(!userPlan?.has_plan || isPlanExpiring); // has no active plans OR has plan but is expiring
	}

	// Warning: this is only safe here because it is continually called
	// dont use this in other places
	private hasNoEligibleBilling (): boolean {
		if (this.routerService.isRouteActive(ROUTE_NAMES.DASHBOARD_FOLDERS)) return false;
		if (this.userService.currentUser.is_root_user) return false;

		return this.group.isCourse() &&
			!this.selectedService.isLicenseLoading &&
			!this.selectedService.getLicense() &&
			!this.selectedService.getOrg().org_settings.orgType.student_pay_enabled;
	}

	private consumeSeat () {
		if(!this.isGroupIsACourse) {
			this.isConsumeSeatLoaded$$.next(true);
			return;
		}

		const group = this.group;

		if (!(group.hasPresenterRole() && !group.requiresPayment() && !group.hasPaid())) {
			this.isConsumeSeatLoaded$$.next(true);
			return;
		}

		this.isConsumeSeatLoaded$$.next(false);
		return this.groupService
			.consumeSeat(this.userService.currentUser.getId(), group.getId())
			.pipe(
				finalize(() => this.isConsumeSeatLoaded$$.next(true))
			)
			.subscribe({
				next: (response: ConsumeSeatResponse) => {
					this.dataSource?.reload();
					this.group.billing_entity_id = response.billing_entity_id;
					// set selected service group beid so we don't consume again
					this.selectedService.setGroup(this.group);
				},
				error: (error) => {
					if (error.status === 402) {
						this.hasConsumingSeatError = true;
					} else {
						this.errorCode = error.status;
					}
				}
			});
	}
}
